import React, { useEffect } from "react";
import { observer } from "mobx-react";
import "primeflex/primeflex.css";
import { MarketersViewModel } from "./MarketersViewModel";
import Utils from "../../infrastructure/Utils";
import { Permission } from "../../infrastructure/enum/Permission";
import { Card } from "primereact/card";
import { IMASMessagesHandler } from "../components/controls/IMASMessagesHandler";
import UserContext from "../../infrastructure/UserContext";
import { respondToAuthCacheRequest } from "../../infrastructure/authCacheRelay";
import { getToken } from "../../infrastructure/Auth0Authenticator";

// Plain function component, deliberately NOT wrapped in observer(): this
// codebase's mobx-react@5 wraps observer() function components as class
// components under the hood, and hooks can't run inside a class component's
// render() (see the abandoned, commented-out useEffect in
// AgentAutoAppointmentsView.tsx for a prior run-in with the same trap).
const MarketersAuthRelay: React.FC<{ marketersBaseUrl: string }> = ({
  marketersBaseUrl,
}) => {
  useEffect(() => {
    // Warm this token now, before the iframe's REQUEST_AUTH_CACHE arrives.
    // getToken() serializes through acquireToken()'s Web Lock (see
    // Auth0Authenticator.ts), which is also queued behind every other API
    // call this page fires on load - asking for it lazily inside
    // respondToAuthCacheRequest means it can lose the child's 2s timeout
    // race on a cold load. Pre-fetching here lets it settle in the
    // background so the real request, once it arrives, resolves instantly.
    getToken().catch(() => {});

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== marketersBaseUrl) return;

      respondToAuthCacheRequest(event, marketersBaseUrl);

      if (event.data && event.data.type === "AGENT_SELECTED_IN_CONTAINER_APP") {
        localStorage.setItem("currentAgentId", event.data.value);
        localStorage.setItem("currentAgentName", event.data.name);
        localStorage.setItem("isAgentSelected", "true");
      } else if (event.data && event.data.type === "WARNING_TOAST") {
        Utils.showWarningToaster(event.data.message, 3000, 500);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [marketersBaseUrl]);

  return null;
};

export const MarketersView: React.FC<{
  vm: MarketersViewModel;
}> = observer(() => {
  let env = Utils.getAppHostKey();
  const impersonatorId = UserContext.getUserId();
  const params = new URLSearchParams({
    tenantName: Utils.getBUName(),
    impersonatorId: String(impersonatorId),
  });
  //  const marketersBaseUrl = "http://localhost:3001"; // enable only for local development
  const marketersBaseUrl =
    `https://marketer-${env}.integrityconnect.com`.replace("-prod", "");
  let marketersUrl = `${marketersBaseUrl}?${params.toString()}`;

  return (
    <>
      {Utils.hasUserPermission(Permission.AccessOutreach) ? (
        <div className="marketers-container">
          <MarketersAuthRelay marketersBaseUrl={marketersBaseUrl} />
          <iframe
            src={marketersUrl}
            id="marketers-iframe"
            frameBorder="0"
            style={{ height: "calc(100vh - 70px)", width: "100%" }}
          />
        </div>
      ) : (
        <Card>
          <IMASMessagesHandler response={Utils.errorMsg()} />
        </Card>
      )}
    </>
  );
});
